/** Frame maths on integer milliseconds; mirrors vp/core/clocks.py exactly (tests pin both). */

export function frameOf(tMs: number, fps: number): number {
  return Math.floor((tMs / 1000) * fps + 1e-6);
}

/** First millisecond inside frame k (rounded up so frameOf(frameStartMs(k)) === k). */
export function frameStartMs(frame: number, fps: number): number {
  const t = (frame * 1000) / fps;
  const ms = Math.floor(t);
  return ms >= t - 1e-9 ? ms : ms + 1;
}

/** Move by n frames from the frame shown at tMs; never below 0. */
export function stepFrames(tMs: number, fps: number, n: number): number {
  return frameStartMs(Math.max(0, frameOf(tMs, fps) + n), fps);
}

export function snapToFrame(tMs: number, fps: number): number {
  return frameStartMs(Math.round((tMs * fps) / 1000), fps);
}

/** Seconds for HTMLMediaElement.currentTime that land inside frame k (mid-frame avoids boundary rounding). */
export function frameSeekSeconds(frame: number, fps: number): number {
  return (frame + 0.5) / fps;
}

/** SMPTE-style HH:MM:SS:FF (non-drop) for display. */
export function timecode(tMs: number, fps: number): string {
  const f = frameOf(tMs, fps);
  const perSec = Math.round(fps);
  const totalSec = Math.floor(f / perSec);
  const ff = f % perSec;
  const hh = Math.floor(totalSec / 3600);
  const mm = Math.floor((totalSec % 3600) / 60);
  const ss = totalSec % 60;
  const p = (x: number) => String(x).padStart(2, "0");
  return `${p(hh)}:${p(mm)}:${p(ss)}:${p(ff)}`;
}

/** m:ss.mmm like the Markdown exports. */
export function fmtMs(tMs: number | null | undefined): string {
  if (tMs == null) return "";
  const sign = tMs < 0 ? "-" : "";
  const t = Math.abs(Math.trunc(tMs));
  return `${sign}${Math.floor(t / 60000)}:${String(Math.floor(t / 1000) % 60).padStart(2, "0")}.${String(t % 1000).padStart(3, "0")}`;
}

/** Nearest grid point (beats, downbeats, line starts); grid sorted ascending. */
export function nearest(tMs: number, grid: readonly number[]): number | null {
  if (grid.length === 0) return null;
  let lo = 0;
  let hi = grid.length - 1;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if ((grid[mid] as number) < tMs) lo = mid + 1;
    else hi = mid;
  }
  let best = grid[lo] as number;
  if (lo > 0 && Math.abs((grid[lo - 1] as number) - tMs) <= Math.abs(best - tMs)) best = grid[lo - 1] as number;
  return best;
}

/** Next/previous grid point strictly after/before tMs (for ±beat, ±line, ±section buttons). */
export function nextOnGrid(tMs: number, grid: readonly number[], dir: 1 | -1): number | null {
  if (dir > 0) return grid.find((g) => g > tMs + 0.5) ?? null;
  for (let i = grid.length - 1; i >= 0; i--) if ((grid[i] as number) < tMs - 0.5) return grid[i] as number;
  return null;
}
